import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  RadioGroup, FormControlLabel, Radio, TextField, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  orderService,
  WMS_CANCEL_REASON_CODES,
  type WmsCancelReasonCode,
} from '../../services/orderService';

export interface CancelOrderResult {
  orderId: string;
  waveStillActive: boolean;
  remainingPendingItems: number;
}

interface CancelOrderDialogProps {
  open: boolean;
  orderId: string;
  orderNumber?: string;
  productName?: string;
  cancelledById?: string;
  cancelledByName?: string;
  onClose: () => void;
  onCancelled: (result: CancelOrderResult) => void;
}

const CancelOrderDialog: React.FC<CancelOrderDialogProps> = ({
  open, orderId, orderNumber, productName, cancelledById, cancelledByName, onClose, onCancelled,
}) => {
  const { t } = useTranslation('warehouse');
  const [reasonCode, setReasonCode] = useState<WmsCancelReasonCode>('stock_missing');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (!orderId || saving) return;
    setSaving(true);
    try {
      const reasonText = t(`screens.picking.cancel.reasons.${reasonCode}`);
      const res = await orderService.cancel(orderId, {
        ReasonCode: reasonCode,
        ReasonText: reasonText,
        Note: note.trim() || undefined,
        Source: 'Picking',
        CancelledById: cancelledById,
        CancelledByName: cancelledByName,
      });
      const data = res.data?.data || res.data?.Data || {};
      onCancelled({
        orderId,
        waveStillActive: Boolean(data.waveStillActive ?? data.WaveStillActive),
        remainingPendingItems: Number(data.remainingPendingItems ?? data.RemainingPendingItems ?? 0),
      });
      setNote('');
      setReasonCode('stock_missing');
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || err?.response?.data?.Message || t('screens.picking.cancel.failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('screens.picking.cancel.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('screens.picking.cancel.orderLabel')}{' '}
          <strong>{orderNumber || orderId.slice(0, 8)}</strong>
        </Typography>
        {productName && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('screens.picking.cancel.productLabel')} <strong>{productName}</strong>
          </Typography>
        )}
        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
          {t('screens.picking.cancel.warning')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('screens.picking.cancel.reasonTitle')}
        </Typography>
        <RadioGroup
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value as WmsCancelReasonCode)}
        >
          {WMS_CANCEL_REASON_CODES.map((code) => (
            <FormControlLabel
              key={code}
              value={code}
              control={<Radio />}
              label={t(`screens.picking.cancel.reasons.${code}`)}
            />
          ))}
        </RadioGroup>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label={t('screens.picking.cancel.noteLabel')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? t('screens.picking.cancel.sending') : t('screens.picking.cancel.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelOrderDialog;
